import { ReactNode, createContext, useState, useEffect } from "react";
import {
  useShareLinkQuery,
  ShareLink,
  ShareLinkTypeEnum,
} from "../../../generated/graphql";
import { LoadingProgressOverlay } from "../../shared/LoadingProgress";

export type ShareLinkFormData = Partial<
  Pick<
    ShareLink,
    | "id"
    | "shortCode"
    | "shareUrl"
    | "type"
    | "name"
    | "description"
    | "isActive"
    | "expiresAt"
    | "targetUlcId"
    | "targetUlc"
    | "externalUrl"
    | "forceContentAccess"
  >
>;

export interface IShareLinkFormModalContext {
  formData: ShareLinkFormData;
  updateFormData: <Key extends keyof ShareLinkFormData>(
    keyOrData: Key | Partial<ShareLinkFormData>,
    value?: ShareLinkFormData[Key],
  ) => void;
}

export const ShareLinkFormModalContext =
  createContext<IShareLinkFormModalContext>({
    formData: {},
    updateFormData: () => {},
  });

export const ShareLinkFormModalProvider = ({
  children,
  id,
}: {
  children: ReactNode;
  id?: string;
}) => {
  const [formData, setFormData] = useState<ShareLinkFormData>({
    type: ShareLinkTypeEnum.Content,
    forceContentAccess: true,
  });

  const { data, loading } = useShareLinkQuery({
    variables: { id: id as string },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.shareLink?.id) {
      setFormData(data.shareLink);
    }
  }, [data]);

  if (loading) {
    return <LoadingProgressOverlay />;
  }

  const updateFormData = <Key extends keyof ShareLinkFormData>(
    keyOrData: Key | Partial<ShareLinkFormData>,
    value?: ShareLinkFormData[Key],
  ) => {
    const update =
      typeof keyOrData === "object" ? keyOrData : { [keyOrData]: value };
    setFormData((prev) => ({ ...prev, ...update }));
  };

  return (
    <ShareLinkFormModalContext.Provider value={{ formData, updateFormData }}>
      {children}
    </ShareLinkFormModalContext.Provider>
  );
};
